import React, { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Play, MessageCircle, Star, X } from "lucide-react";
import { Section, Eyebrow, Reveal } from "./shared";
import { TESTIMONIALS, IMAGES, TESTIMONIAL_VIDEO } from "../../lib/site";

const whatsappNotes = [
  "Got my first consultation inquiry from a 4-month-old video!",
  "Finally understand which topics to make videos on.",
  "Sold my PDF to someone who found me on search.",
  "The title library alone is worth it.",
];

export default function Testimonials() {
  const [videoOpen, setVideoOpen] = useState(false);
  return (
    <Section id="testimonials" bg="bg-brand-cream">
      <Reveal className="max-w-3xl">
        <Eyebrow color="#4CAF48">Testimonials</Eyebrow>
        <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl leading-[0.98] mt-5">
          Clarity Changes the Way You Take Action
        </h2>
        <p className="text-sm text-brand-ink/60 mt-4">Hear it from Ratish&rsquo;s own students and community.</p>
      </Reveal>

      <div className="grid lg:grid-cols-3 gap-6 mt-12 items-start">
        {/* Video testimonial (large) */}
        <Reveal className="lg:row-span-2">
          <button onClick={() => setVideoOpen(true)} className="w-full text-left rounded-[26px] border-2 border-brand-black bg-brand-black overflow-hidden h-full min-h-[340px] relative group cursor-pointer" style={{ boxShadow: "8px 10px 0 #E90019" }} data-testid="testimonial-video">
            <img src={TESTIMONIAL_VIDEO.thumb} alt="Watch the student testimonial video" loading="lazy" className="w-full h-full object-cover opacity-70 group-hover:opacity-90 transition-opacity" />
            <div className="absolute inset-0 flex flex-col items-center justify-center gap-4 bg-brand-black/20">
              <span className="h-16 w-16 rounded-full bg-brand-red flex items-center justify-center border-2 border-white group-hover:scale-110 transition-transform">
                <Play size={26} className="text-white fill-white ml-1" />
              </span>
              <p className="text-brand-cream font-display text-xl">Watch Student Story</p>
            </div>
          </button>
        </Reveal>


        {/* Written testimonials — varied */}
        {TESTIMONIALS.map((t, i) => (
          <Reveal key={t.name} delay={i * 0.08} className={t.size === "wide" ? "lg:col-span-2" : ""}>
            <div className={`rounded-[24px] border-2 border-brand-black bg-white p-6 ${t.size === "tall" ? "min-h-[280px]" : ""}`}
              style={{ boxShadow: `6px 8px 0 ${["#0B31EB", "#FFCB52", "#4CAF48"][i % 3]}` }}>
              <div className="flex text-brand-yellow mb-3">{[...Array(5)].map((_, s) => <Star key={s} size={15} className="fill-brand-yellow" />)}</div>
              <p className="text-sm"><span className="font-bold">Before:</span> {t.before}</p>
              <p className="text-sm mt-2 text-brand-blue"><span className="font-bold text-brand-ink">Clarity:</span> {t.clear}</p>
              <p className="text-sm mt-2"><span className="font-bold">Result:</span> {t.result}</p>
              <div className="flex items-center gap-3 mt-5 pt-4 border-t-2 border-brand-black/10">
                <img src={IMAGES[t.img]} alt={t.name} loading="lazy" className="h-11 w-11 rounded-full object-cover border-2 border-brand-black" />
                <div>
                  <p className="font-bold text-sm">{t.name}</p>
                  <p className="text-xs text-brand-ink/60">{t.role}</p>
                </div>
              </div>
            </div>
          </Reveal>
        ))}
      </div>

      {/* WhatsApp gallery */}
      <Reveal delay={0.1}>
        <div className="mt-8">
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-brand-green mb-4 flex items-center gap-2"><MessageCircle size={14} /> From the community</p>
          <div className="flex gap-4 overflow-x-auto pb-4 -mx-1 px-1 snap-x" data-testid="whatsapp-gallery">
            {whatsappNotes.map((w, i) => (
              <div key={i} className="snap-start shrink-0 w-[260px] rounded-[20px] rounded-tl-none border-2 border-brand-black bg-brand-palegreen p-4 text-sm">
                <p>{w}</p>
                <p className="text-[10px] text-brand-ink/50 mt-2 text-right">12:0{i} ✓✓</p>
              </div>
            ))}
          </div>
        </div>
      </Reveal>

      <AnimatePresence>
        {videoOpen && (
          <motion.div className="fixed inset-0 z-[100] flex items-center justify-center p-4 sm:p-6 bg-brand-black/85"
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            onClick={() => setVideoOpen(false)} data-testid="testimonial-video-modal">
            <button className="absolute top-6 right-6 h-11 w-11 rounded-full bg-white border-2 border-brand-black flex items-center justify-center" aria-label="Close" data-testid="testimonial-video-close">
              <X size={20} />
            </button>
            <motion.div className="w-full max-w-[900px] aspect-video rounded-2xl overflow-hidden border-2 border-white"
              initial={{ scale: 0.92 }} animate={{ scale: 1 }} onClick={(e) => e.stopPropagation()}>
              <iframe className="w-full h-full" src={TESTIMONIAL_VIDEO.embed} title="Student testimonial"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowFullScreen />
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </Section>
  );
}
